import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import apiClient from '@/api/client'
import { useCategories } from '@/hooks/useCategories'
import { toastHelpers } from '@/lib/toast-helpers'
import type { Product, Tag } from '@/types'
import { cn } from '@/lib/utils'

// A small palette so operators don't have to think about hex codes. Values are
// stored verbatim in tags.color and reused as the chip colour everywhere.
export const TAG_COLORS = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EC4899', '#6B7280']

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** null = create, a Tag = edit that tag (name/colour/description + membership). */
  editing: Tag | null
  /** Called after a successful save so the caller can refresh the report. */
  onSaved: () => void
}

/**
 * Tag create/edit for Reports. Beyond the label fields (name/colour/description)
 * this owns the tag-centric item picker: the whole menu as a searchable,
 * category-filterable checkbox list, so you assign items to a tag from the place
 * you actually read the numbers — no round-trip to the Menu page.
 */
export function TagManagerDialog({ open, onOpenChange, editing, onSaved }: Props) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [color, setColor] = useState(TAG_COLORS[3])
  const [description, setDescription] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')
  const [categoryId, setCategoryId] = useState('all')
  const [prefillLoading, setPrefillLoading] = useState(false)

  // Full menu + categories for the picker, fetched only while the dialog is open.
  const productsQuery = useQuery({
    queryKey: ['tag-picker-products'],
    queryFn: () => apiClient.getAdminProducts({ per_page: 1000 }).then((r) => r.data ?? []),
    enabled: open,
    staleTime: 60_000,
  })
  const products = (productsQuery.data as Product[] | undefined) ?? []

  // Shared ['categories'] cache via the single-source-of-truth hook (F-FE-N6):
  // guaranteed Category[]. Only fetch while the dialog is open.
  const categoriesQuery = useCategories({ enabled: open })
  const categories = categoriesQuery.data ?? []
  const categoryName = useMemo(() => {
    const m = new Map<string, string>()
    categories.forEach((c) => m.set(c.id, c.name))
    return m
  }, [categories])

  // Reset the form and seed the checkbox selection each time the dialog opens.
  // On edit we pull the tag's current membership so the right boxes start ticked.
  useEffect(() => {
    if (!open) return
    setSearch('')
    setCategoryId('all')
    if (editing) {
      setName(editing.name)
      setColor(editing.color || TAG_COLORS[3])
      setDescription(editing.description || '')
      setPrefillLoading(true)
      apiClient
        .getTagProducts(editing.id)
        .then((r) => setSelected(new Set(r.data ?? [])))
        .catch((err) => toastHelpers.apiError('Load tag items', err))
        .finally(() => setPrefillLoading(false))
    } else {
      setName('')
      setColor(TAG_COLORS[3])
      setDescription('')
      setSelected(new Set())
    }
  }, [open, editing])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return products.filter((p) => {
      if (categoryId !== 'all' && p.category_id !== categoryId) return false
      if (q && !p.name.toLowerCase().includes(q)) return false
      return true
    })
  }, [products, search, categoryId])

  const allFilteredSelected = filtered.length > 0 && filtered.every((p) => selected.has(p.id))

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const toggleAllFiltered = () =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (allFilteredSelected) filtered.forEach((p) => next.delete(p.id))
      else filtered.forEach((p) => next.add(p.id))
      return next
    })

  const saveMutation = useMutation({
    mutationFn: async () => {
      const trimmed = name.trim()
      const payload = { name: trimmed, color: color || null, description: description.trim() || null }
      // Resolve the tag id first (create returns it), then write membership.
      let tagId = editing?.id
      if (editing) {
        const res = await apiClient.updateTag(editing.id, payload)
        if (!res.success) throw new Error(res.message || 'Failed to save tag')
      } else {
        const res = await apiClient.createTag(payload)
        if (!res.success || !res.data?.id) throw new Error(res.message || 'Failed to create tag')
        tagId = res.data.id
      }
      const assign = await apiClient.setTagProducts(tagId!, [...selected])
      if (!assign.success) throw new Error(assign.message || 'Failed to assign items')
      return trimmed
    },
    onSuccess: (savedName) => {
      queryClient.invalidateQueries({ queryKey: ['report-tags'] })
      queryClient.invalidateQueries({ queryKey: ['reports-v2'] })
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      onSaved()
      onOpenChange(false)
      toastHelpers.success(editing ? 'Tag updated' : 'Tag created', savedName)
    },
    onError: (err: unknown) => toastHelpers.apiError('Save tag', err),
  })

  const canSave = name.trim().length > 0 && !saveMutation.isPending && !prefillLoading

  return (
    <Dialog open={open} onOpenChange={(o) => !saveMutation.isPending && onOpenChange(o)}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit tag' : 'New tag'}</DialogTitle>
          <DialogDescription>
            Tags are reporting labels — assign items here to segment the report; categories and sales
            totals are never changed.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Name</label>
              <Input
                autoFocus
                placeholder="e.g. Summer Special"
                value={name}
                maxLength={100}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Colour</label>
              <div className="flex flex-wrap gap-2 pt-1">
                {TAG_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-label={`Colour ${c}`}
                    onClick={() => setColor(c)}
                    className={cn(
                      'h-7 w-7 rounded-full border-2 transition',
                      color === c ? 'border-foreground' : 'border-transparent',
                    )}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Description (optional)</label>
            <Input
              placeholder="What does this tag track?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Item picker */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Items in this tag</label>
              <span className="text-xs text-muted-foreground">{selected.size} selected</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-56">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search items…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-lg border">
              <div className="flex items-center gap-3 border-b bg-muted/40 px-3 py-2">
                <Checkbox
                  checked={allFilteredSelected}
                  onCheckedChange={toggleAllFiltered}
                  disabled={filtered.length === 0}
                  aria-label="Select all shown"
                />
                <span className="text-xs text-muted-foreground">
                  Select all shown ({filtered.length})
                </span>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {productsQuery.isLoading || prefillLoading ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">Loading items…</div>
                ) : filtered.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    No items match your filters.
                  </div>
                ) : (
                  <ul className="divide-y">
                    {filtered.map((p) => (
                      <li key={p.id}>
                        <label className="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-muted/30">
                          <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggle(p.id)} />
                          <span className="min-w-0 flex-1 truncate text-sm">{p.name}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">
                            {p.category_id ? categoryName.get(p.category_id) ?? '—' : '—'}
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={() => saveMutation.mutate()} disabled={!canSave}>
            {saveMutation.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create tag'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
